import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "@/components/site/Footer";
import { blogPosts } from "@/data/blog-posts";
import { SITE_URL, SITE_NAME } from "@/lib/seo-data";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const BlogIndex = () => {
  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: `${SITE_NAME} — Journal`,
    url: `${SITE_URL}/blog`,
    blogPost: blogPosts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.date,
      url: `${SITE_URL}/blog/${p.slug}`,
    })),
  };

  return (
    <main className="bg-background overflow-x-hidden">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
      </Helmet>

      <header className="relative bg-jungle text-background overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/90 via-jungle/70 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70 hidden sm:inline">Sojourns</span>
            </Link>
            <Link
              to="/"
              className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors"
            >
              ← Return Home
            </Link>
          </div>
          <div className="container pt-20 pb-24 md:pt-28 md:pb-32 max-w-4xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Journal</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Notes from <em className="font-light">the road</em> in Cambodia.
            </h1>
            <p className="mt-10 max-w-xl text-background/70 font-light leading-relaxed text-base">
              Long-form essays on Angkor, Siem Reap, the Tonlé Sap and the art of slow travel in Cambodia — written by a local French-speaking guide.
            </p>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-16">
            {blogPosts.map((post) => (
              <article key={post.slug} className="group">
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="relative overflow-hidden mb-6 aspect-[5/4] bg-secondary shadow-soft">
                    <img
                      src={post.cover}
                      alt={post.coverAlt}
                      width={1200}
                      height={960}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
                    <time dateTime={post.date}>{formatDate(post.date)}</time>
                    <span>·</span>
                    <span>{post.readingMinutes} min read</span>
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl leading-tight text-foreground group-hover:text-primary transition-colors mb-3">
                    {post.title}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">{post.excerpt}</p>
                  <span className="mt-5 inline-block text-[11px] uppercase tracking-[0.3em] border-b border-foreground/30 pb-1 group-hover:border-primary group-hover:text-primary transition-colors">
                    Read article →
                  </span>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default BlogIndex;
